import { AsyncValidatorFn } from '@angular/forms';
import { Observable } from 'rxjs';
import { DateCollectionKeyEnum } from '../date-collection/date-collection.component';

export type ControlType = 'text' | 'select' | 'textarea' | 'state' | 'checkbox' | 'dateCollection' | 'phone' | 'empty';

export const COUNTRY_CODE = 'CountryCode';
export interface DynamicFormDropdownOption {
	/**
	 * The label you want to use for the option. This will show up when the dropdown opens.
	 */
	label: string;
	/**
	 * The value to use for the selected item.
	 */
	value: string;
}

export interface DynamicFormControlOptions<T> {
	/**
	 * The value you want the browser to autocomplete with.
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete}
	 */
	autocomplete?: string;
	/**
	 * The control type
	 */
	controlType: ControlType;
	/**
	 * Custom css class
	 */
	cssClass?: string;
	/**
	 * If this is a state input, you can use this to enter the key for the country select and it will modify the form for you
	 * to handle changing to a dropdown for CA, US, etc.
	 */
	countryKey?: string;
	/**
	 * Allows you to format the date collection input.
	 */
	dateCollectionUse?: DateCollectionKeyEnum[];
	/**
	 * If this is a `input` control type and its supposed to be for collecting credit card expiration dates.
	 */
	creditCardExpiration?: boolean;
	/**
	 * The key for the control.
	 */
	key: string;
	/**
	 * If you want to display a hint. Should be a string with `section.target` as it will be piped through the locale pipe.
	 */
	hint?: string;
	/**
	 * If you want to use a prefixed icon.
	 */
	iconPrefix?: string;
	/**
	 * If you want to use a suffixed icon.
	 */
	iconSuffix?: string;
	/**
	 * The label you want to display. Should be a string with `section.target` as it will be piped through the locale pipe.
	 */
	label?: string;
	/**
	 * Do you want to hide the label?
	 */
	hideLabel?: boolean;
	/**
	 * Hides the form control if the control is in disabled state
	 */
	hideOnDisabled?: boolean;
	/**
	 * The placeholder you want to display. Should be a string with `section.target` as it will be piped through the locale pipe.
	 */
	placeholder?: string;
	/**
	 * This is the size of the input. Should be any number between 1 and 100.
	 */
	size?: number;
	/**
	 * This is the size of the input on mobile. Should be any number between 1 and 100.
	 */
	sizeXs?: number;
	/**
	 * If this is a controlType of `phone`, you can pass the country code here.
	 */
	telCode?: string;
	/**
	 * If this is a `input` control type, you can set the attribute `type` to what you'd like. For example, 'tel'.
	 */
	type?: string;
	/**
	 * A comma-delimited list of transforms to apply.
	 */
	transform?: string;
	/**
	 * A comma-delimited list of form validators to apply. These are typically cross-field validators.
	 */
	formValidators?: string;
	/**
	 * A comma-delimited list of validators to apply.
	 */
	validators?: string;
	/**
	 * The value you want to set.
	 */
	value?: T;
	/**
	 * If you're using a `select` control type, these are the options.
	 */
	options?: DynamicFormDropdownOption[];
	/**
	 * If you're using a `select` control type, this is an observable we'll use to input the options.
	 */
	asyncOptions?: Observable<DynamicFormDropdownOption[]>;
	/**
	 * Any sort of async validators you want on the input
	 */
	asyncValidators?: AsyncValidatorFn[];
	/**
	 * If this is a `dateCollection` control type, you can restrict by minimum age.
	 */
	minAge?: number;
	/**
	 * If this is a `dateCollection` control type, you can restrict by minimum date.
	 */
	minDate?: string | Date;
	/**
	 * If this is a `dateCollection` control type, you can restrict by maximum age.
	 */
	maxAge?: number;
	/**
	 * If this is a `dateCollection` control type, you can restrict by maximum date.
	 */
	maxDate?: string | Date;
	/**
	 * for handling enter press in a form field
	 */
	onEnterPressed?: () => void;
	/**
	 * space keydown callback
	 */
	onKeyDownSpace?: ($event: KeyboardEvent) => void;
}
